import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useQueryClient } from '@tanstack/react-query'
import CreateChooserModal from './CreateChooserModal'
import CreateRestaurantModal from './CreateRestaurantModal'
import howIEatService from '../services/howIEatService'
import { useTranslation } from '../hooks/use-i18n'
import { useNavSpaces } from '../hooks/useNavSpaces'

const DashboardHomeContent: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showRestaurantTab, showHowIEatTab, howIEatSpace, isLoading } = useNavSpaces()
  const [showChooser, setShowChooser] = useState(false)
  const [showCreateRestaurant, setShowCreateRestaurant] = useState(false)
  const [isCreatingHowIEat, setIsCreatingHowIEat] = useState(false)

  const handleHowIEatAction = async () => {
    try {
      setIsCreatingHowIEat(true)
      if (howIEatSpace) {
        setShowChooser(false)
        navigate(`/how-i-eat/${howIEatSpace._id || howIEatSpace.id}`)
        return
      }
      const space = await howIEatService.createSpace()
      setShowChooser(false)
      await queryClient.invalidateQueries({ queryKey: ['how-i-eat', 'me'] })
      navigate(`/how-i-eat/${space._id || space.id}`)
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.failedToOpenSpace'))
    } finally {
      setIsCreatingHowIEat(false)
    }
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-6 sm:mb-8">
        <div className="text-center sm:text-left flex-1">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2 sm:mb-4">
            {t('nav.home')}
          </h1>
          <p className="text-white/60 text-sm sm:text-base">{t('home.hub.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => setShowChooser(true)}
          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-2.5 sm:py-3 rounded-lg transition-all duration-300 font-semibold text-sm sm:text-base w-full sm:w-auto shrink-0"
        >
          {t('dashboard.createButton')}
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500" />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 max-w-3xl">
          {showRestaurantTab ? (
            <Link
              to="/restaurants"
              className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-5 hover:bg-white/10 transition-colors"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-500/15 text-2xl">🍽️</div>
              <div>
                <p className="font-semibold text-white">{t('nav.restaurant')}</p>
                <p className="text-sm text-white/60">{t('home.hub.restaurantDesc')}</p>
              </div>
            </Link>
          ) : null}
          {showHowIEatTab && howIEatSpace ? (
            <Link
              to={`/how-i-eat/${howIEatSpace._id || howIEatSpace.id}`}
              className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-5 hover:bg-white/10 transition-colors"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/15 text-2xl">🥗</div>
              <div>
                <p className="font-semibold text-white">{t('nav.howIEat')}</p>
                <p className="text-sm text-white/60">{t('home.hub.howIEatDesc')}</p>
              </div>
            </Link>
          ) : null}
          {!showRestaurantTab && !showHowIEatTab ? (
            <div className="sm:col-span-2 rounded-2xl border border-white/10 bg-white/5 p-6 text-center text-white/65">
              {t('home.hub.emptyHint')}
            </div>
          ) : null}
        </div>
      )}

      {showChooser && (
        <CreateChooserModal
          onClose={() => setShowChooser(false)}
          isCreatingHowIEat={isCreatingHowIEat}
          hasHowIEatSpace={!!howIEatSpace}
          onCreateRestaurant={() => {
            setShowChooser(false)
            setShowCreateRestaurant(true)
          }}
          onCreateHowIEat={handleHowIEatAction}
        />
      )}

      {showCreateRestaurant && (
        <CreateRestaurantModal
          onClose={() => setShowCreateRestaurant(false)}
          onSuccess={() => setShowCreateRestaurant(false)}
        />
      )}
    </>
  )
}

export default DashboardHomeContent
